import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button, LoadingButton } from 'base-ui'

import {
  contactFormHandleContactFormSubmit,
  type ContactFormState,
  type useContactFormApiPostMutation,
} from '../../../contact-form'
import { type OpenableControls } from '../../../generic-hooks'
import { useUserApiSwrSessionV1Customer } from '../../../user-data'

import { useDirectFormDialogContext } from './DirectContactFormDialogContext'

export const DirectContactFormSubmitButton: FC<{
  formState: ContactFormState
  isFriendlyCaptchaDone: boolean
  modalControls: OpenableControls
  onFormSubmittedWithSuccess: () => void
  submitMutation: ReturnType<typeof useContactFormApiPostMutation>
}> = ({
  formState,
  isFriendlyCaptchaDone,
  modalControls,
  onFormSubmittedWithSuccess,
  submitMutation,
}) => {
  const intl = useIntl()

  const {
    productList,
    setProductList,
    setSubject,
    setSubOrderNumber,
    setUserData,
  } = useDirectFormDialogContext()

  const { data: userData } = useUserApiSwrSessionV1Customer()
  const isLoggedIn = Boolean(userData?.customerNumber)

  const { prefilledSubOrder } = useDirectFormDialogContext()

  const selectedItemPzns = [...productList].map((obj) => obj.pzn).join(', ')

  // This is a temporary solution to enable the sending of other details, e.g phone number, productpzn etc, to customer support
  // because the current setup with AX does not support adding additional fields
  const contactFormMessage = (): void => {
    formState.validate()
    if (!formState.isValid()) {
      return
    }

    if (
      formState.model.phoneNumber &&
      !formState.model.message.includes(formState.model.phoneNumber)
    ) {
      const newMessage = `${formState.model.message} (phoneNumber: ${formState.model.phoneNumber})`

      formState.setValue(newMessage, formState.model, 'message')
    }

    if (
      productList.length > 0 &&
      !formState.model.message.includes(selectedItemPzns)
    ) {
      const newMessage = `${formState.model.message} (Product Pzns: [${selectedItemPzns}])`

      formState.setValue(newMessage, formState.model, 'message')
    }
  }

  if (
    formState.model.subject ===
      intl.formatMessage({ id: 'direct.contact.form.change.name' }) ||
    (!isLoggedIn &&
      formState.model.subject ===
        intl.formatMessage({ id: 'direct.contact.form.delete.account' }))
  ) {
    return (
      <div className="flex flex-col justify-center gap-3 tablet:flex-row">
        <Button
          className="mb-2 w-full tablet:w-1/2"
          data-qa-id="contact-form-submit-cancel-button"
          onClick={() => {
            setSubject('')
            modalControls.handleClose()
          }}
          variant="outlined"
        >
          <FormattedMessage id="general.button.cancel" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col justify-center gap-3 tablet:flex-row">
      <LoadingButton
        className="mb-2 w-full"
        data-qa-id="contact-form-submit-button"
        disabled={!isFriendlyCaptchaDone}
        loading={submitMutation.isMutating}
        onClick={(event): void => {
          event.preventDefault()
          contactFormMessage()
          void contactFormHandleContactFormSubmit(
            formState,
            submitMutation,
            (successValue) => {
              if (successValue) {
                onFormSubmittedWithSuccess()
                setProductList([])
                setSubject('')
                setUserData({})
                if (!prefilledSubOrder) {
                  setSubOrderNumber('')
                }
              }
            },
          )
        }}
        type="submit"
      >
        <FormattedMessage id="product.consultation.button.submitMessage" />
      </LoadingButton>
      <Button
        className="mb-2 w-full tablet:w-1/2"
        data-qa-id="contact-form-submit-cancel-button"
        onClick={() => {
          setSubject('')
          modalControls.handleClose()
        }}
        variant="outlined"
      >
        <FormattedMessage id="general.button.cancel" />
      </Button>
    </div>
  )
}
